import { ActionIcon, Tabs, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { useCatalogLabels } from '../labels';
import { ViewModeToggle } from './ViewModeToggle';

interface ToolbarProps {
    search: string;
    onSearch: (value: string) => void;
    viewMode: 'grid' | 'list';
    onViewMode: (value: 'grid' | 'list') => void;
    mode: 'products' | 'documents';
    onMode: (value: 'products' | 'documents') => void;
    /** Whether anything in the filter drawer is set - the trigger is then drawn filled. No count
     * on it any more: it only ever counted the drawer's own filters, while the chip row below shows
     * every filter (scope, column filters too), so "2" on the icon sat next to four chips
     * (reported). The chips say what is set; the icon only says "something is".
     * Omitting `onOpenFilters` (e.g. no facets configured/available) hides the trigger entirely. */
    filtersActive?: boolean;
    onOpenFilters?: () => void;
    /** Hides the grid/list toggle even in products mode - CatalogPage sets this while a System
     * node is selected, since that view renders SystemDetailContent (with its own, separately
     * scoped Ansicht toggle) instead of the grid this one controls; showing both at once looked
     * like a broken duplicate control (see chat: code review finding). */
    hideViewMode?: boolean;
    /** How many rows the list below currently holds. Sits right after the search controls, so the
     * term and the number it produced stay next to each other. Omitted (rather than 0) whenever this
     * toolbar does not actually govern the pane below it - e.g. while a System leaf shows its own
     * three-tab view - so the bar never states a number the user cannot see. */
    resultCount?: number;
    /** The list's column picker, rendered at the row's right end next to the view/filter controls.
     * Passed in rather than built here because which columns exist is the caller's business (the
     * catalog list and a system's Artikelliste offer the same set but hold their own state). */
    columns?: ReactNode;
    /** The category tree's trigger on narrow screens (the tree is a drawer there). Rendered in the
     * control row, ahead of the count, instead of as a loose row of its own between the toolbar
     * and the list - the caller hides it from `md` up, where the tree is a permanent sidebar. */
    navigation?: ReactNode;
    /** The active filters as removable chips (ActiveFilterChips) - rendered as the toolbar's last
     * row, right under the controls that set them, and only when there is anything to show. */
    filterChips?: ReactNode;
    /** Search-wide actions ("Link teilen", "Suchprofile"), at the right end of the tab row - the one
     * row with room to spare on every width. */
    actions?: ReactNode;
    /** Shortest term that searches (config.search.minChars) - below it a hint replaces the count. */
    minChars?: number;
}

/**
 * Catalog search and list controls: a tab row for the corpus, then ONE control row.
 *
 * Two changes got it here. The original was a single `flex-wrap` row carrying five different
 * control metaphors at equal rank - radio buttons for the mode, a `grow` text field, a captioned
 * SegmentedControl, a radio group with icon labels, an ActionIcon with a count badge - which is why
 * "Suche in:" wrapped onto two lines and the right-hand controls ended up glued together. Lifting
 * the mode switch out into tabs fixed that (it is navigation, not a form field). A second, tinted
 * tier for the list controls did not: it bought a clearer grouping at the price of a third band of
 * chrome above every list, and the grouping was not worth that (reported: "Die zusätzliche Zeile
 * bringt uns eigentlich nichts").
 *
 * So one row, read left to right as: what you search (term), what came back (count), and
 * how it is shown (view, columns, filters) pushed to the far end. Distance does the grouping that
 * the separate tier used to do, without costing a row.
 */
export const Toolbar = ({
    search,
    onSearch,
    viewMode,
    onViewMode,
    mode,
    onMode,
    filtersActive,
    onOpenFilters,
    hideViewMode,
    resultCount,
    columns,
    navigation,
    filterChips,
    actions,
    minChars,
}: ToolbarProps) => {
    const { L } = useCatalogLabels();
    const [value, setValue] = useState(search);
    const [debounced] = useDebouncedValue(value, 250);

    useEffect(() => {
        setValue(search);
    }, [search]);

    useEffect(() => {
        if (debounced !== search) {
            onSearch(debounced);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    // The actions are centred on the TAB LABELS, measured: centred on the whole tab block they sat
    // low, just above its bottom rule (reported: "Die Icons ... sollten etwas höher sein,
    // zentriert") - @uag/doka-design gives the tabs extra room below the label.
    const tabsRef = useRef<HTMLDivElement>(null);
    const [actionsBand, setActionsBand] = useState<{ top: number; height: number }>();
    useLayoutEffect(() => {
        const wrapper = tabsRef.current;
        if (!wrapper || !actions) {
            return undefined;
        }
        const measure = () => {
            // The label text itself where Mantine marks it, else the whole tab.
            const label = wrapper.querySelector('[role="tab"] .mantine-Tabs-tabLabel') ?? wrapper.querySelector('[role="tab"]');
            if (!label) {
                return;
            }
            const outer = wrapper.getBoundingClientRect();
            const inner = label.getBoundingClientRect();
            // A band of at least 36px around the label's middle - room for the 34px buttons.
            const height = Math.max(36, Math.round(inner.height));
            const middle = inner.top - outer.top + (inner.height / 2);
            setActionsBand((prev) => {
                const next = { top: Math.round(middle - (height / 2)), height };
                return prev && prev.top === next.top && prev.height === next.height ? prev : next;
            });
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(wrapper);
        return () => observer.disconnect();
    }, [actions]);

    const showViewMode = mode === 'products' && !hideViewMode;
    const typed = value.trim().length;
    const tooShort = minChars !== undefined && typed > 0 && typed < minChars;

    return (
        <div className="flex flex-col">
            {/* The actions sit beside the tab list, not in it: a tablist may only hold tabs. Laid
                over its right end so the list's bottom rule still runs the full width. */}
            <div ref={tabsRef} className="relative">
                <Tabs
                    className="pih-mode-tabs"
                    value={mode}
                    onChange={(next) => next && onMode(next as 'products' | 'documents')}
                >
                    <Tabs.List px="xs">
                        <Tabs.Tab value="products">{L.searchModeProducts}</Tabs.Tab>
                        <Tabs.Tab value="documents">{L.searchModeDocuments}</Tabs.Tab>
                    </Tabs.List>
                </Tabs>
                {actions && (
                    <div
                        className="absolute right-2 flex items-center"
                        style={actionsBand ?? { top: 0, bottom: 0 }}
                    >
                        {actions}
                    </div>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                {/* Grows into the row, but capped: a search box has no use for 1800px of width, and
                    left uncapped it took the whole line (reported: "Es gibt keinen Grund, das
                    Suchfeld über die ganze Breite zu ziehen. Das war davor besser."). The cap is
                    what leaves room for the count beside it.
                    No magnifier icon: the placeholder already says what the field is for, and the
                    icon only ate into the writing width (reported). */}
                <TextInput
                    aria-label={L.searchPlaceholder}
                    placeholder={L.searchPlaceholder}
                    size="sm"
                    // 200px basis, not 280: on a phone the count has to fit beside it on the
                    // first line (see `navigation` below). On wider rows it grows to 520 either way.
                    style={{ flex: '1 1 200px', minWidth: 200, maxWidth: 520 }}
                    value={value}
                    onChange={(event) => setValue(event.currentTarget.value)}
                />
                {/* Right next to the controls that produced it, so term and result read as
                    one sentence. `nowrap` because "575 Treffer" breaking after the number is the
                    one thing that would make this row look accidental. */}
                {/* A single typed character does not search yet (minChars) - the list stays as it
                    was, which read as "the search does not work" (review point 10). Said here, in
                    place of a count that would describe the unsearched list. */}
                {tooShort ? (
                    <Text c="dimmed" size="sm" style={{ whiteSpace: 'nowrap' }}>
                        {L.searchMinChars.replace('{count}', String(minChars))}
                    </Text>
                ) : (
                    resultCount !== undefined && (
                        <Text c="dimmed" size="sm" style={{ whiteSpace: 'nowrap' }}>
                            {resultCount} {L.results}
                        </Text>
                    )
                )}

                {/* After the count, not before it: on a phone the search field and the count share
                    the first line, the categories button and the display controls the second - with
                    the button in front of the count, a four-digit "3772 Treffer" pushed the display
                    controls onto a line of their own. Hidden from `md` up anyway. */}
                {navigation}

                {/* Pushes the display controls to the far end. That gap is what separates "what you
                    searched" from "how it is shown" now that they share a row - the tinted second
                    tier used to do it with a rule and a background, for the price of a whole band
                    of chrome above every list. */}
                <div className="grow" />

                {/* Three spacing steps, widest to narrowest, so the grouping reads without any
                    rules or tinting: 16px in front of the display controls (the row's own gap-2
                    plus pl-2) because they are a different kind of thing from the search controls,
                    12px between the Ansicht switch and the two buttons (one control vs. two), 8px
                    between those buttons. An even run of four icons would read as one
                    undifferentiated cluster. */}
                {/* `ml-auto`: should the row wrap on a narrow screen, the display controls still
                    land on the right edge instead of dangling at the start of a line of their
                    own. The extra 8px lead only pays off on a wide row. */}
                <div className="ml-auto flex items-center gap-3 sm:pl-2">
                    {showViewMode && <ViewModeToggle value={viewMode} onChange={onViewMode} />}
                    <div className="flex items-center gap-2">
                        {columns}
                        {onOpenFilters && (
                            <ActionIcon
                                aria-label={L.filters}
                                aria-pressed={Boolean(filtersActive)}
                                title={L.filters}
                                variant={filtersActive ? 'filled' : 'default'}
                                onClick={onOpenFilters}
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                                    tune
                                </span>
                            </ActionIcon>
                        )}
                    </div>
                </div>
            </div>

            {/* Same horizontal padding as the control row above, so the chips start on the search
                field's left edge; a hairline separates "what is set" from "what you can set". */}
            {filterChips && (
                <div className="px-3 py-2" style={{ borderTop: '1px solid var(--pih-border)' }}>
                    {filterChips}
                </div>
            )}
        </div>
    );
};
